import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DateTimePicker } from 'react-widgets';
import moment from 'moment';
import { LabelItem } from '../Forms/LabelItem';
import { InputTheme } from '../Forms/InputStyled';
import theme from '../../assets/css/theme';
import { UncontrolledTooltip } from 'reactstrap';
import * as Popper from 'popper.js';

const LabelItemStyled = styled(LabelItem)`
  width: fit-content;
`;

const Wrapper = styled.div`
  margin-bottom: 10px;

  .rw-widget-picker {
    border-radius: 20px;
    border: 1px solid transparent;

    &:not(:last-child) {
      border-right: none;
    }

    &::placeholder {
      font-weight: normal;
      color: ${theme.commonColors.main};
      opacity: 1;
    }

    &:focus {
      padding-left: 10px;
    }

    ${InputTheme.main};
  }

  .rw-select {
    border-left: none;
    padding-right: 4px;

    &:hover {
      background: #ffffff;
    }

    .rw-btn {
      color: ${theme.commonColors.second};
    }
  }

  .tooltip-icon {
    color: ${theme.commonColors.second};
    margin-left: 5px;
  }
`;

interface IProps {
  readonly date: string | null;
  readonly placeholder?: string;
  readonly className?: string;
  readonly callback: (value: string) => void;
  readonly label?: string;
  readonly defaultDate?: string | Date | null;
  readonly withTime?: boolean;
  readonly withTooltip?: boolean;
  readonly id?: string;
  readonly tooltipPlacement?: Popper.Placement;
  readonly tooltipText?: string;
  readonly wrapperClassName?: string;
  readonly format?: string;
  readonly minDate?: Date;
  readonly maxDate?: Date;
  readonly dropUp?: boolean;
  readonly disabled?: boolean;
  readonly preserveInvalidDate?: boolean;
  // Extra moment formats react-widgets should accept when parsing typed input
  // (on top of the display format). Lets users type e.g. slashes as well as dots.
  readonly parse?: string[] | ((value: string, culture?: string) => Date | null);
}

export const DatePicker = ({
  date,
  placeholder,
  className,
  callback,
  label,
  defaultDate = null,
  withTime = false,
  withTooltip,
  id,
  tooltipPlacement,
  wrapperClassName,
  tooltipText,
  minDate,
  maxDate,
  dropUp = false,
  disabled = false,
  format,
  parse,
  preserveInvalidDate = false
}: IProps) => {
  const [internalDate, setInternalDate] = useState<Date | null>(() => {
    if (!defaultDate) return null;
    const initialDate =
      defaultDate instanceof Date
        ? defaultDate
        : typeof defaultDate === 'string'
        ? moment(defaultDate).toDate()
        : null;

    return initialDate || null;
  });

  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!defaultDate) return;
    const parsedDate = date ? moment(date).toDate() : null;
    if (!parsedDate) {
      setInternalDate(null);

      return;
    }

    if (!moment(parsedDate).isSame(internalDate, withTime ? 'minute' : 'day')) {
      setInternalDate(parsedDate);
    }
  }, [date, defaultDate, withTime]);

  const handleChange = (value: Date | null) => {
    if (!value) {
      if (preserveInvalidDate) {
        if (internalDate) {
          const restored = new Date(internalDate.getTime());
          setInternalDate(restored);
          callback(restored.toISOString());
        }

        return;
      }
      setInternalDate(null);
      callback('');

      return;
    }

    if (defaultDate) {
      setInternalDate(value);
    }
    callback(value.toISOString());
  };

  const handleBlur = () => {
    if (!preserveInvalidDate) {
      return;
    }

    if (pickerRef.current) {
      const input = pickerRef.current.querySelector('input') as HTMLInputElement | null;
      if (!input) return;

      const parsedDate = moment(
        input.value,
        ['DD.MM.YYYY', 'MM/DD/YYYY', moment.ISO_8601],
        true
      ).toDate();

      if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
        if (internalDate) {
          const formatted = moment(internalDate).format(format || 'DD.MM.YYYY');
          input.value = formatted;

          callback(internalDate.toISOString());
        } else {
          input.value = '';
          callback('');
        }
      }
    }
  };

  const tooltipID = `${id}_tooltip`;
  const tooltipIcon =
    withTooltip && id ? <i className="fa fa-icon fa-question-circle tooltip-icon" /> : null;
  const tooltip =
    withTooltip && id ? (
      <UncontrolledTooltip
        data-cy="tooltipText"
        autohide={false}
        placement={tooltipPlacement}
        target={tooltipID}
        dangerouslySetInnerHTML={{ __html: tooltipText || '' }}
      />
    ) : null;

  const useNewMode = !!defaultDate;
  const pickerDate = useNewMode ? internalDate : date ? moment(date).toDate() : null;

  return (
    <Wrapper className={wrapperClassName} ref={pickerRef}>
      {label ? (
        <LabelItemStyled isSmall isBold id={tooltipID}>
          {label}
          {tooltipIcon}
        </LabelItemStyled>
      ) : null}
      {tooltip}
      <DateTimePicker
        data-cy={`dateTimePicker-${id}`}
        time={withTime}
        value={pickerDate}
        placeholder={placeholder}
        className={className}
        onChange={handleChange}
        onBlur={handleBlur}
        min={minDate}
        max={maxDate}
        dropUp={dropUp}
        disabled={disabled}
        format={format}
        parse={parse}
      />
    </Wrapper>
  );
};

export default DatePicker;
